import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Globe,
  RefreshCw,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ExternalLink,
  FileText,
  Search,
  Loader2,
  Unplug,
  BarChart3,
  Clock,
  Info,
  Eye,
  EyeOff,
  Wand2,
  Undo2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { api } from '../../context/AuthContext';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import PlatformIcons from '../../components/icons/PlatformIcons';

const PLATFORM_META = {
  wordpress:   { label: 'WordPress',   color: 'bg-blue-100 text-blue-700',   Icon: PlatformIcons.wordpress },
  shopify:     { label: 'Shopify',     color: 'bg-green-100 text-green-700', Icon: PlatformIcons.shopify },
  wix:         { label: 'Wix',         color: 'bg-purple-100 text-purple-700', Icon: PlatformIcons.wix },
  squarespace: { label: 'Squarespace', color: 'bg-slate-100 text-slate-700', Icon: PlatformIcons.squarespace },
  custom:      { label: 'Custom CMS',  color: 'bg-amber-100 text-amber-700', Icon: PlatformIcons.custom },
};

const STATUS_BADGES = {
  connected:     { label: 'Connected',     color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  not_connected: { label: 'Not Connected', color: 'bg-slate-100 text-slate-600',     icon: Unplug },
  failed:        { label: 'Failed',        color: 'bg-red-100 text-red-700',         icon: XCircle },
  expired:       { label: 'Expired',       color: 'bg-amber-100 text-amber-700',     icon: AlertCircle },
  testing:       { label: 'Testing...',    color: 'bg-sky-100 text-sky-700',         icon: Loader2 },
};

const PLATFORMS = [
  { id: 'wordpress',   name: 'WordPress',          Icon: PlatformIcons.wordpress, authType: 'credentials' },
  { id: 'shopify',     name: 'Shopify',            Icon: PlatformIcons.shopify,   authType: 'oauth' },
  { id: 'wix',         name: 'Wix',                Icon: PlatformIcons.wix,       authType: 'oauth' },
  { id: 'squarespace', name: 'Squarespace',        Icon: PlatformIcons.squarespace, authType: 'credentials' },
];

const CREDENTIAL_FIELDS = {
  wordpress: {
    description: 'Enter your WordPress site URL and an Application Password.',
    fields: [
      { key: 'site_url', label: 'Site URL', placeholder: 'https://yoursite.com', type: 'url' },
      { key: 'username', label: 'Username', placeholder: 'admin', type: 'text' },
      { key: 'app_password', label: 'Application Password', placeholder: 'xxxx xxxx xxxx xxxx', type: 'password' },
    ],
  },
  squarespace: {
    description: 'Enter your Squarespace site URL and API Key from Developer settings.',
    fields: [
      { key: 'site_url', label: 'Site URL', placeholder: 'https://yoursite.com', type: 'url' },
      { key: 'api_key', label: 'API Key', placeholder: 'Your Squarespace API key', type: 'password' },
    ],
  },
};

function ConnectCmsForm({ onConnected }) {
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [credentials, setCredentials] = useState({});
  const [shopUrl, setShopUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [connectStatus, setConnectStatus] = useState(null);
  const [showPasswords, setShowPasswords] = useState({});

  const platformInfo = PLATFORMS.find(p => p.id === selectedPlatform);
  const isOAuth = platformInfo?.authType === 'oauth';
  const isCredentials = platformInfo?.authType === 'credentials';
  const credConfig = CREDENTIAL_FIELDS[selectedPlatform];

  const handleFieldChange = (key, value) => {
    setCredentials(prev => ({ ...prev, [key]: value }));
    if (connectStatus) setConnectStatus(null);
  };

  const handleTestConnection = async () => {
    if (!credConfig) return;
    const missing = credConfig.fields.filter(f => !credentials[f.key]?.trim());
    if (missing.length > 0) {
      toast.error(`Please fill in: ${missing.map(f => f.label).join(', ')}`);
      return;
    }
    setLoading(true);
    setConnectStatus('testing');
    try {
      const siteUrl = credentials.site_url;
      const creds = { ...credentials };
      delete creds.site_url;
      await api.post('/cms/connect', { platform: selectedPlatform, site_url: siteUrl, credentials: creds });
      setConnectStatus('connected');
      toast.success('CMS connected!');
      onConnected();
    } catch (err) {
      setConnectStatus('failed');
      toast.error(err.response?.data?.detail || 'Connection failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthRedirect = async () => {
    if (selectedPlatform === 'shopify' && !shopUrl.trim()) {
      toast.error('Please enter your Shopify store URL');
      return;
    }
    setLoading(true);
    try {
      const redirectUri = `${window.location.origin}/cms/callback`;
      // Tell CmsOAuthCallback which platform is being connected
      sessionStorage.setItem('cms_oauth_platform', selectedPlatform);

      let resp;
      if (selectedPlatform === 'shopify') {
        resp = await api.get('/cms/shopify/auth-url', { params: { shop: shopUrl.trim(), redirect_uri: redirectUri } });
      } else {
        resp = await api.get('/cms/wix/auth-url', { params: { redirect_uri: redirectUri } });
      }
      if (resp?.data?.auth_url) window.location.href = resp.data.auth_url;
    } catch (err) {
      sessionStorage.removeItem('cms_oauth_platform');
      toast.error(err.response?.data?.detail || 'Failed to start connection');
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <div className="text-center mb-6">
        <div className="w-16 h-16 rounded-2xl bg-indigo-100 flex items-center justify-center mx-auto mb-4">
          <Globe className="w-8 h-8 text-indigo-600" />
        </div>
        <h2 className="text-xl font-bold mb-2">Connect Your Website CMS</h2>
        <p className="text-slate-500 max-w-md mx-auto">
          Connect your CMS to pull page data, analyze meta tags, and identify SEO opportunities.
        </p>
      </div>

      <Card className="gds-card">
        <CardHeader>
          <CardTitle>Select your platform</CardTitle>
        </CardHeader>
        <CardContent>
          <RadioGroup value={selectedPlatform} onValueChange={(v) => { setSelectedPlatform(v); setCredentials({}); setConnectStatus(null); setShopUrl(''); }} className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {PLATFORMS.map((p) => {
              const PIcon = p.Icon;
              return (
                <div key={p.id}>
                  <RadioGroupItem value={p.id} id={`cms-${p.id}`} className="peer sr-only" />
                  <Label htmlFor={`cms-${p.id}`} className="flex flex-col items-center justify-center rounded-xl border-2 border-slate-200 bg-white p-3 hover:bg-slate-50 peer-data-[state=checked]:border-indigo-600 peer-data-[state=checked]:bg-indigo-50 cursor-pointer transition-all">
                    <PIcon className="w-6 h-6 mb-1" />
                    <span className="text-xs font-medium">{p.name}</span>
                  </Label>
                </div>
              );
            })}
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Credential form: WordPress / Squarespace */}
      {isCredentials && credConfig && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="gds-card">
            <CardHeader>
              <CardTitle>Connect to {platformInfo.name}</CardTitle>
              <CardDescription>{credConfig.description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {credConfig.fields.map((field) => (
                <div key={field.key}>
                  <Label htmlFor={`f-${field.key}`}>{field.label}</Label>
                  <div className="relative mt-1">
                    <Input
                      id={`f-${field.key}`}
                      type={field.type === 'password' && !showPasswords[field.key] ? 'password' : 'text'}
                      placeholder={field.placeholder}
                      value={credentials[field.key] || ''}
                      onChange={(e) => handleFieldChange(field.key, e.target.value)}
                      disabled={connectStatus === 'connected'}
                    />
                    {field.type === 'password' && (
                      <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" onClick={() => setShowPasswords(prev => ({ ...prev, [field.key]: !prev[field.key] }))}>
                        {showPasswords[field.key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {connectStatus && (
                <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${connectStatus === 'connected' ? 'bg-emerald-50 text-emerald-700' : connectStatus === 'failed' ? 'bg-red-50 text-red-700' : 'bg-sky-50 text-sky-700'}`}>
                  {connectStatus === 'connected' && <CheckCircle2 className="w-4 h-4" />}
                  {connectStatus === 'failed' && <XCircle className="w-4 h-4" />}
                  {connectStatus === 'testing' && <Loader2 className="w-4 h-4 animate-spin" />}
                  <span>
                    {connectStatus === 'connected' && 'Connected! Your CMS is verified.'}
                    {connectStatus === 'failed' && 'Connection failed. Check your credentials.'}
                    {connectStatus === 'testing' && 'Connecting to your CMS...'}
                  </span>
                </div>
              )}
              <Button onClick={handleTestConnection} disabled={loading || connectStatus === 'connected'} className={connectStatus === 'connected' ? 'bg-emerald-600' : 'gds-btn-primary'}>
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Connecting...</> : connectStatus === 'connected' ? <><CheckCircle2 className="w-4 h-4 mr-2" /> Connected</> : 'Connect'}
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* OAuth: Shopify / Wix */}
      {isOAuth && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="gds-card">
            <CardHeader>
              <CardTitle>Connect to {platformInfo.name}</CardTitle>
              <CardDescription>
                You'll be redirected to {platformInfo.name} to authorize GDS. No passwords needed.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedPlatform === 'shopify' && (
                <div>
                  <Label htmlFor="shop-url-cms">Your Shopify store URL</Label>
                  <Input id="shop-url-cms" placeholder="my-store.myshopify.com" value={shopUrl} onChange={(e) => setShopUrl(e.target.value)} className="mt-1" />
                </div>
              )}
              <Button onClick={handleOAuthRedirect} disabled={loading} className="gds-btn-primary">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting...</> : <><ExternalLink className="w-4 h-4 mr-2" /> Connect {platformInfo.name}</>}
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </div>
  );
}

function MetaStatus({ value }) {
  if (value) {
    return <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">Present</Badge>;
  }
  return <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200 text-xs">Missing</Badge>;
}

function SeoHealthCard({ label, present, total, type }) {
  const pct = total > 0 ? Math.round((present / total) * 100) : 0;
  const missing = total - present;
  return (
    <Card className="gds-card">
      <CardContent className="pt-5 pb-4">
        <p className="text-sm text-slate-500 mb-1">{label}</p>
        <div className="flex items-end gap-2 mb-2">
          <span className="text-2xl font-bold">{present}</span>
          <span className="text-sm text-slate-400">/ {total} {type}</span>
        </div>
        <Progress value={pct} className="h-2 mb-1" />
        {missing > 0 && (
          <p className="text-xs text-red-500 mt-1">{missing} missing</p>
        )}
      </CardContent>
    </Card>
  );
}

export default function CmsDashboardPage() {
  const [connection, setConnection] = useState(null);
  const [siteInfo, setSiteInfo] = useState(null);
  const [pages, setPages] = useState([]);
  const [posts, setPosts] = useState([]);
  const [seoHealth, setSeoHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const [fixModal, setFixModal] = useState(null);
  const [fixLoading, setFixLoading] = useState(false);
  const [pushingFix, setPushingFix] = useState(false);
  const [changeLog, setChangeLog] = useState([]);
  const [connectorStatus, setConnectorStatus] = useState(null);

  const isConnected = connection?.status === 'connected';
  const platform = PLATFORM_META[connection?.platform] || PLATFORM_META.custom;
  const statusBadge = STATUS_BADGES[connection?.status] || STATUS_BADGES.not_connected;
  const StatusIcon = statusBadge.icon;

  const fetchStatus = useCallback(async () => {
    try {
      const resp = await api.get('/cms/status');
      setConnection(resp.data);
      return resp.data;
    } catch {
      setConnection(null);
      return null;
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [infoResp, pagesResp, postsResp, healthResp] = await Promise.all([
        api.get('/cms/site-info').catch(() => null),
        api.get('/cms/pages').catch(() => null),
        api.get('/cms/posts').catch(() => null),
        api.get('/cms/seo-health').catch(() => null),
      ]);
      if (infoResp) setSiteInfo(infoResp.data);
      if (pagesResp) setPages(pagesResp.data);
      if (postsResp) setPosts(postsResp.data);
      if (healthResp) setSeoHealth(healthResp.data);
    } catch (err) {
      console.error('CMS data fetch error:', err);
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const conn = await fetchStatus();
      if (conn?.status === 'connected') {
        await fetchData();
        if (conn?.platform === 'wordpress') {
          api.get('/cms/connector-status').then((r) => setConnectorStatus(r.data)).catch(() => {});
        }
      }
      setLoading(false);
    })();
  }, [fetchStatus, fetchData]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await api.post('/cms/sync');
      toast.success('CMS data synced successfully');
      await fetchData();
      await fetchStatus();
    } catch (err) {
      let msg;
      if (!err.response) {
        msg = "Sync couldn't reach the server. Please check your connection and try again.";
      } else if (err.response.status === 401 || err.response.status === 403) {
        msg = "Your CMS credentials may have expired. Please reconnect your CMS in Integrations.";
      } else {
        msg = err.response?.data?.detail || "Sync ran into an issue. Please try again — or reconnect your CMS if the problem persists.";
      }
      toast.error(msg, { duration: 6000 });
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await api.post('/cms/disconnect');
      toast.success('CMS disconnected');
      setConnection(null);
      setSiteInfo(null);
      setPages([]);
      setPosts([]);
      setSeoHealth(null);
    } catch {
      toast.error('Failed to disconnect');
    }
  };

  const handleAiFix = async (itemType, slug) => {
    setFixLoading(true);
    try {
      const res = await api.post(`/cms/ai-fix/${itemType}/${slug}`);
      setFixModal(res.data);
    } catch (err) {
      let msg;
      if (!err.response) {
        msg = "Couldn't generate the AI fix — server is unreachable. Please check your connection and try again.";
      } else if (err.response.status >= 500) {
        msg = "The AI fix couldn't be generated right now. Please try again in a moment, or edit the meta tags manually in your CMS.";
      } else {
        msg = err.response?.data?.detail || "Couldn't generate the fix. You can edit the meta tags directly in your CMS as an alternative.";
      }
      toast.error(msg, { duration: 6000 });
    } finally {
      setFixLoading(false);
    }
  };

  const handlePushFix = async () => {
    if (!fixModal) return;
    setPushingFix(true);
    try {
      await api.post('/cms/push-and-log', {
        item_type: fixModal.item_type,
        slug: (fixModal.slug || '').replace(/\/+$/, ''),
        meta_title: fixModal.suggested.meta_title,
        meta_description: fixModal.suggested.meta_description,
      });
      toast.success('Meta tags pushed to CMS!');

      const cleanSlug = (fixModal.slug || '').replace(/\/+$/, '').replace(/^\/+/, '');
      const updater = (items) => items.map((item) => {
        const itemSlug = (item.slug || '').replace(/\/+$/, '').replace(/^\/+/, '');
        if (itemSlug === cleanSlug) {
          return {
            ...item,
            meta_title: fixModal.suggested.meta_title || item.meta_title,
            meta_description: fixModal.suggested.meta_description || item.meta_description,
          };
        }
        return item;
      });
      if (fixModal.item_type === 'page') {
        setPages(updater);
      } else {
        setPosts(updater);
      }

      setFixModal(null);
      fetchChangeLog();
    } catch (err) {
      let msg;
      if (!err.response) {
        msg = "Couldn't push the fix — server is unreachable. Please try again or apply the changes in your CMS directly.";
      } else if (err.response.status === 401 || err.response.status === 403) {
        msg = "Your CMS connection may have expired. Please reconnect in Integrations and try again.";
      } else {
        msg = err.response?.data?.detail || "The fix couldn't be pushed to your CMS. You can copy the suggested values and update them manually.";
      }
      toast.error(msg, { duration: 6000 });
    } finally {
      setPushingFix(false);
    }
  };

  const fetchChangeLog = async () => {
    try {
      const res = await api.get('/cms/change-log');
      setChangeLog(res.data);
    } catch { /* non-critical */ }
  };

  const handleRollback = async (changeId) => {
    try {
      await api.post(`/cms/rollback/${changeId}`);
      toast.success('Change rolled back');
      fetchChangeLog();
      handleSync();
    } catch (err) {
      let msg;
      if (!err.response) {
        msg = "Couldn't reach the server to roll back. Please try again or revert the change manually in your CMS.";
      } else {
        msg = err.response?.data?.detail || "Rollback failed. You can revert this change manually in your CMS using the previous values shown above.";
      }
      toast.error(msg, { duration: 6000 });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (!isConnected) {
    return <ConnectCmsForm onConnected={async () => {
      setLoading(true);
      const conn = await fetchStatus();
      if (conn?.status === 'connected') {
        await new Promise(r => setTimeout(r, 4000));
        await fetchData();
        setTimeout(fetchData, 8000);
      }
      setLoading(false);
    }} />;
  }

  return (
    <div className="space-y-6">
      {/* Connection Status Bar */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <Card className="gds-card">
          <CardContent className="py-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                  <platform.Icon className="w-7 h-7" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="font-bold text-lg">{siteInfo?.site_name || platform.label}</h2>
                    <Badge className={statusBadge.color}>
                      <StatusIcon className="w-3 h-3 mr-1" />
                      {statusBadge.label}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-slate-500 mt-0.5">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${platform.color}`}>
                      {platform.label}
                    </span>
                    {connection.site_url && (
                      <a
                        href={connection.site_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 hover:text-indigo-600 transition-colors"
                      >
                        {connection.site_url.replace(/^https?:\/\//, '')}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    {connection.last_synced && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Synced {formatDistanceToNow(new Date(connection.last_synced), { addSuffix: true })}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSync}
                  disabled={syncing}
                >
                  <RefreshCw className={`w-4 h-4 mr-1 ${syncing ? 'animate-spin' : ''}`} />
                  {syncing ? 'Syncing...' : 'Sync'}
                </Button>
                <Button variant="outline" size="sm" onClick={handleDisconnect} className="text-red-600 hover:text-red-700">
                  <Unplug className="w-4 h-4 mr-1" />
                  Disconnect
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* GDS Connector Plugin Banner (WordPress only) */}
      {connection?.platform === 'wordpress' && connectorStatus && !connectorStatus.installed && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className="border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/20">
            <CardContent className="py-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-medium text-amber-800 dark:text-amber-200">Install GDS Connector Plugin</p>
                    <p className="text-sm text-amber-600 dark:text-amber-400">
                      Required for auto-executing SEO changes (title tags, meta descriptions, schema markup).
                      Install it once in your WordPress admin under Plugins → Add New → Upload Plugin.
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  className="border-amber-300 text-amber-700 hover:bg-amber-100 shrink-0"
                  onClick={() => {
                    const backendUrl = api.defaults.baseURL?.replace('/api', '') || '';
                    window.open(`${backendUrl}/api/cms/plugin-download`, '_blank');
                  }}
                >
                  <Globe className="w-4 h-4 mr-2" />
                  Download Plugin
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {connection?.platform === 'wordpress' && connectorStatus?.installed && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className="border-emerald-200 dark:border-emerald-800 bg-emerald-50/30 dark:bg-emerald-900/10">
            <CardContent className="py-3">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                <p className="text-sm text-emerald-700 dark:text-emerald-400">
                  <span className="font-medium">GDS Connector active</span>
                  {connectorStatus.seo_plugin && connectorStatus.seo_plugin !== 'none' && (
                    <> — detected <span className="font-medium">{connectorStatus.seo_plugin === 'rankmath' ? 'Rank Math' : 'Yoast SEO'}</span></>
                  )}
                  {connectorStatus.version && <span className="text-emerald-500"> (v{connectorStatus.version})</span>}
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-4 w-full max-w-md">
          <TabsTrigger value="overview" className="flex items-center gap-1">
            <BarChart3 className="w-4 h-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="pages" className="flex items-center gap-1">
            <FileText className="w-4 h-4" />
            Pages
          </TabsTrigger>
          <TabsTrigger value="posts" className="flex items-center gap-1">
            <FileText className="w-4 h-4" />
            Posts
          </TabsTrigger>
          <TabsTrigger value="seo" className="flex items-center gap-1">
            <Search className="w-4 h-4" />
            SEO
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6 space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="gds-card">
              <CardContent className="pt-5 pb-4 text-center">
                <p className="text-3xl font-bold">{siteInfo?.page_count ?? pages.length}</p>
                <p className="text-sm text-slate-500">Pages</p>
              </CardContent>
            </Card>
            <Card className="gds-card">
              <CardContent className="pt-5 pb-4 text-center">
                <p className="text-3xl font-bold">{siteInfo?.post_count ?? posts.length}</p>
                <p className="text-sm text-slate-500">Blog Posts</p>
              </CardContent>
            </Card>
            <Card className="gds-card">
              <CardContent className="pt-5 pb-4 text-center">
                <p className="text-3xl font-bold">{siteInfo?.has_blog ? 'Yes' : 'No'}</p>
                <p className="text-sm text-slate-500">Has Blog</p>
              </CardContent>
            </Card>
            <Card className="gds-card">
              <CardContent className="pt-5 pb-4 text-center">
                <p className="text-3xl font-bold">
                  {siteInfo?.has_seo_plugin ? (
                    <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto" />
                  ) : siteInfo?.has_seo_plugin === false ? (
                    <XCircle className="w-8 h-8 text-red-400 mx-auto" />
                  ) : (
                    <span className="text-slate-400">--</span>
                  )}
                </p>
                <p className="text-sm text-slate-500">
                  {siteInfo?.seo_plugin_name || 'SEO Plugin'}
                </p>
              </CardContent>
            </Card>
          </div>

          {siteInfo && (
            <Card className="gds-card">
              <CardHeader>
                <CardTitle>Site Details</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                  <div>
                    <dt className="text-slate-500">Platform</dt>
                    <dd className="font-medium">{platform.label}</dd>
                  </div>
                  {siteInfo.platform_version && (
                    <div>
                      <dt className="text-slate-500">Version</dt>
                      <dd className="font-medium">{siteInfo.platform_version}</dd>
                    </div>
                  )}
                  <div>
                    <dt className="text-slate-500">Site URL</dt>
                    <dd className="font-medium">
                      <a href={siteInfo.site_url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline flex items-center gap-1">
                        {siteInfo.site_url} <ExternalLink className="w-3 h-3" />
                      </a>
                    </dd>
                  </div>
                  {siteInfo.seo_plugin_name && (
                    <div>
                      <dt className="text-slate-500">SEO Plugin</dt>
                      <dd className="font-medium text-emerald-600">{siteInfo.seo_plugin_name}</dd>
                    </div>
                  )}
                </dl>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Pages Tab */}
        <TabsContent value="pages" className="mt-6">
          <Card className="gds-card">
            <CardHeader>
              <CardTitle>Pages ({pages.length})</CardTitle>
              <CardDescription>All pages pulled from your {platform.label} site</CardDescription>
            </CardHeader>
            <CardContent>
              {pages.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p>No pages found. Click Sync to refresh data.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>URL</TableHead>
                        <TableHead className="text-center">Meta Title</TableHead>
                        <TableHead className="text-center">Meta Desc</TableHead>
                        <TableHead className="text-center">Words</TableHead>
                        <TableHead>Updated</TableHead>
                        <TableHead className="text-center">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pages.map((page, idx) => (
                        <TableRow key={idx} className={!page.meta_title || !page.meta_description ? 'bg-red-50/50' : ''}>
                          <TableCell className="font-medium max-w-[200px] truncate">{page.title}</TableCell>
                          <TableCell className="max-w-[200px]">
                            <a href={page.url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline text-sm truncate block">
                              /{page.slug}
                            </a>
                          </TableCell>
                          <TableCell className="text-center"><MetaStatus value={page.meta_title} /></TableCell>
                          <TableCell className="text-center"><MetaStatus value={page.meta_description} /></TableCell>
                          <TableCell className="text-center text-sm">{page.word_count}</TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {page.updated_at ? formatDistanceToNow(new Date(page.updated_at), { addSuffix: true }) : '--'}
                          </TableCell>
                          <TableCell className="text-center">
                            {(!page.meta_title || !page.meta_description) && (
                              <Button size="sm" variant="outline" onClick={() => handleAiFix('page', page.slug)} disabled={fixLoading}
                                className="text-xs">
                                {fixLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Wand2 className="h-3 w-3 mr-1" />AI Fix</>}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Posts Tab */}
        <TabsContent value="posts" className="mt-6">
          <Card className="gds-card">
            <CardHeader>
              <CardTitle>Blog Posts ({posts.length})</CardTitle>
              <CardDescription>All blog posts from your {platform.label} site</CardDescription>
            </CardHeader>
            <CardContent>
              {posts.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p>No blog posts found. Click Sync to refresh data.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>URL</TableHead>
                        <TableHead className="text-center">Meta Title</TableHead>
                        <TableHead className="text-center">Meta Desc</TableHead>
                        <TableHead className="text-center">Words</TableHead>
                        <TableHead>Author</TableHead>
                        <TableHead>Published</TableHead>
                        <TableHead className="text-center">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {posts.map((post, idx) => (
                        <TableRow key={idx} className={!post.meta_title || !post.meta_description ? 'bg-red-50/50' : ''}>
                          <TableCell className="font-medium max-w-[200px] truncate">{post.title}</TableCell>
                          <TableCell className="max-w-[200px]">
                            <a href={post.url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline text-sm truncate block">
                              /{post.slug}
                            </a>
                          </TableCell>
                          <TableCell className="text-center"><MetaStatus value={post.meta_title} /></TableCell>
                          <TableCell className="text-center"><MetaStatus value={post.meta_description} /></TableCell>
                          <TableCell className="text-center text-sm">{post.word_count}</TableCell>
                          <TableCell className="text-sm text-slate-500">{post.author || '--'}</TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {post.published_at ? formatDistanceToNow(new Date(post.published_at), { addSuffix: true }) : '--'}
                          </TableCell>
                          <TableCell className="text-center">
                            {(!post.meta_title || !post.meta_description) && (
                              <Button size="sm" variant="outline" onClick={() => handleAiFix('post', post.slug)} disabled={fixLoading}
                                className="text-xs">
                                {fixLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Wand2 className="h-3 w-3 mr-1" />AI Fix</>}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* SEO Health Tab */}
        <TabsContent value="seo" className="mt-6 space-y-6">
          {seoHealth ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <SeoHealthCard label="Pages with Meta Title" present={seoHealth.pages_with_meta_title} total={seoHealth.total_pages} type="pages" />
                <SeoHealthCard label="Pages with Meta Description" present={seoHealth.pages_with_meta_description} total={seoHealth.total_pages} type="pages" />
                <SeoHealthCard label="Pages with Schema" present={seoHealth.pages_with_schema} total={seoHealth.total_pages} type="pages" />
                <SeoHealthCard label="Posts with Meta Title" present={seoHealth.posts_with_meta_title} total={seoHealth.total_posts} type="posts" />
                <SeoHealthCard label="Posts with Meta Description" present={seoHealth.posts_with_meta_description} total={seoHealth.total_posts} type="posts" />
              </div>

              {(seoHealth.pages_missing_meta_title > 0 || seoHealth.pages_missing_meta_description > 0) && (
                <Card className="gds-card border-amber-200 bg-amber-50/30">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-amber-700">
                      <AlertCircle className="w-5 h-5" />
                      SEO Issues Found
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 text-sm">
                      {seoHealth.pages_missing_meta_title > 0 && (
                        <li className="flex items-center gap-2">
                          <XCircle className="w-4 h-4 text-red-500" />
                          <span>{seoHealth.pages_missing_meta_title} page{seoHealth.pages_missing_meta_title !== 1 ? 's' : ''} missing meta title</span>
                        </li>
                      )}
                      {seoHealth.pages_missing_meta_description > 0 && (
                        <li className="flex items-center gap-2">
                          <XCircle className="w-4 h-4 text-red-500" />
                          <span>{seoHealth.pages_missing_meta_description} page{seoHealth.pages_missing_meta_description !== 1 ? 's' : ''} missing meta description</span>
                        </li>
                      )}
                      {seoHealth.posts_missing_meta_title > 0 && (
                        <li className="flex items-center gap-2">
                          <XCircle className="w-4 h-4 text-red-500" />
                          <span>{seoHealth.posts_missing_meta_title} post{seoHealth.posts_missing_meta_title !== 1 ? 's' : ''} missing meta title</span>
                        </li>
                      )}
                      {seoHealth.posts_missing_meta_description > 0 && (
                        <li className="flex items-center gap-2">
                          <XCircle className="w-4 h-4 text-red-500" />
                          <span>{seoHealth.posts_missing_meta_description} post{seoHealth.posts_missing_meta_description !== 1 ? 's' : ''} missing meta description</span>
                        </li>
                      )}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card className="gds-card text-center py-12">
              <CardContent>
                <Search className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                <p className="text-slate-500">No SEO data available. Click Sync to pull data from your CMS.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* AI Fix Preview Modal */}
      {fixModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setFixModal(null)} />
          <div className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-lg mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-heading font-semibold flex items-center gap-2">
                <Wand2 className="w-5 h-5 text-indigo-600" />AI Fix Preview
              </h2>
              <button onClick={() => setFixModal(null)} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              AI-suggested SEO improvements for <strong>/{fixModal.slug}</strong>
            </p>
            {fixModal.reasoning && (
              <p className="text-xs text-slate-500 bg-slate-50 dark:bg-slate-800 p-2 rounded-lg mb-4">{fixModal.reasoning}</p>
            )}
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-400 uppercase">Meta Title</label>
                <div className="mt-1 grid grid-cols-2 gap-2">
                  <div className="p-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                    <p className="text-[10px] font-semibold text-red-500 mb-1">Current</p>
                    <p className="text-sm">{fixModal.current.meta_title || <span className="text-red-400 italic">Missing</span>}</p>
                  </div>
                  <div className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                    <p className="text-[10px] font-semibold text-emerald-500 mb-1">Suggested</p>
                    <p className="text-sm">{fixModal.suggested.meta_title}</p>
                  </div>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-400 uppercase">Meta Description</label>
                <div className="mt-1 grid grid-cols-2 gap-2">
                  <div className="p-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                    <p className="text-[10px] font-semibold text-red-500 mb-1">Current</p>
                    <p className="text-xs">{fixModal.current.meta_description || <span className="text-red-400 italic">Missing</span>}</p>
                  </div>
                  <div className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                    <p className="text-[10px] font-semibold text-emerald-500 mb-1">Suggested</p>
                    <p className="text-xs">{fixModal.suggested.meta_description}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setFixModal(null)}>Cancel</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handlePushFix} disabled={pushingFix}>
                {pushingFix ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Push to CMS
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
